











































































































































































































































import { Component, Vue, Watch, Ref, PropSync } from "vue-property-decorator";
import RightDrawer from "@/components/misc/RightDrawer.vue";
import CalendarBoxRow from "@/components/calendar/CalendarBoxRow.vue";
import ContingentSettingsForm from "@/components/contingent/ContingentSettingsForm.vue";
import BoxTimeTrack from "@/components/calendar/BoxTimeTrack.vue";
import BoxDayTrack from "@/components/calendar/BoxDayTrack.vue";
import CalendarEditor from "@/components/calendar/CalendarEditor.vue";
import { dragscroll } from "vue-dragscroll";
import { DateTime } from "luxon";
import { Action, Getter } from "vuex-class";
import { JwtClaims } from "@/store/modules/auth";
import { policyChecker } from "@/shared/roles";
import { ContingentDateSpans, ContingentSettings } from "@/store/modules/contingent";
import { ContingentBoxConfig, 
  ContingentDataRequest, 
  ContingentExpandableRow, 
  ContingentMonthView, 
  ContingentRatePlanView, 
  ContingentRowIdentifier, 
  ContingentUnitTypeView, 
  ContingentWindow
} from "./ContingentTypes";
import { DemandChangingEventStatus } from "@/shared/models/demandChangingEvent";
import { GlobalFilterProperty } from "@/store/modules/globalFilters";

const LEFT_W = 264;

const _cell = {
  w: 48,
  h: 48,
  hl: 32,
};

const _config: ContingentBoxConfig = {
  cell: _cell,
  preload: 10,
};

@Component({
  components: {
    RightDrawer,
    ContingentSettingsForm,
    CalendarBoxRow,
    BoxTimeTrack,
    BoxDayTrack,
    CalendarEditor,
  },
  directives: {
    dragscroll,
  },
})
export default class ContingentBox extends Vue {
  @PropSync("openModal") isOpen?: boolean;

  @Getter("contingent/settings") settingsList?: ContingentSettings[];
  @Getter("auth/claims") claims?: JwtClaims;
  @Getter("language/language") language?: string;
  @Getter("codebooks/demandChangingEventStatus") demandEventStatuses?: DemandChangingEventStatus[];
  @Action("codebooks/refreshDemandChangingEventStatus") refreshDemandChangingEventStatus?: () => void;

  get allowedToModifyCalendar() {
    return policyChecker("MODIFY_CALENDAR")(this.claims);
  }

  get currentTenantId() {
    return this.claims ? +this.claims.tid : 0;
  }

  get currentUserId() {
    return this.claims ? +this.claims.sid : 0;
  }

  get currentSettings() {
    const settings = this.settingsList?.find(x => x.userId == this.currentUserId && x.tenantId == this.currentTenantId);
    if (settings) {
      return settings;
    }
    return this.settingsList?.find((x) => x.userId == 0);
  }
  get dateSpan(): ContingentDateSpans {
    return this.currentSettings?.dateSpan ?? "month";
  }
  get propertyId(): number {
    return this.currentSettings?.selectedPropertyId ?? 0;
  }
  get ratePlanId(): number {
    return this.currentSettings?.selectedRatePlanId ?? 0;
  }
  get showPrice(): boolean {
    return this.currentSettings?.showPrice ?? false;
  }
  
  @Getter("contingent/settingsOpen") settingsOpen?: boolean;
  @Action("contingent/openSettings") open?: () => void;
  @Action("contingent/closeSettings") close?: () => void;
  get internalSettingsOpen() {
    return this.settingsOpen ?? false;
  }
  set internalSettingsOpen(val: boolean) {
    if (val) {
      if (this.open) this.open();
    } else {
      if (this.close) this.close();
    }
  }

  config = _config;

  selection: { row: any[]; selection: [DateTime, DateTime] | null } = {
    row: [],
    selection: null,
  };

  checkSelection(month: number, unitType: number) {
    const m = this.dateSpan == "week" ? 0 : month;
    const appliesTo = this.selection.row.findIndex((x) => x.month == m && x.unitType == unitType)
    return appliesTo === -1 ? false : true;
  }

  @Ref("editor") editor?: CalendarEditor;
  @Ref("ratePlanTrack") ratePlanTrack?: BoxDayTrack[];

  async created() {
    const promises = [];

    if((this.demandEventStatuses?.length ?? 0) === 0 && this.refreshDemandChangingEventStatus) {
      promises.push(this.refreshDemandChangingEventStatus());
    }

    await Promise.all(promises);
  }

  contingentData: ContingentMonthView[] = [];

  window: ContingentWindow = {
    w: 0,
    h: 0,
    l: this.config.preload * this.config.cell.w,
  };

  leftEdgeDay = DateTime.local().startOf("month");
  rightEdgeDay = this.leftEdgeDay;

  get fullBox() {
    return {
      w:
        (this.dateSpan == 'week' ? 7 : 31) *
          this.config.cell.w +
        LEFT_W,
      h: this.contingentData.reduce((rAcc, r) => rAcc + r.height, 32),
    };
  }
  get fullBoxStyle() {
    return { width: this.fullBox.w + "px", height: this.fullBox.h + "px" };
  }

  get boxDayStartIdx() {
    return 0;
  }

  get months() {
    let firstDateMonth;
    let secondDateMonth;
    let month;

    if (this.dateSpan === "week" && this.language) {
      //First day + 6 days from start equals week
      firstDateMonth = this.leftEdgeDay
        .plus({
          days: this.boxDayStartIdx,
        })
        .setLocale(this.language).monthLong;

      secondDateMonth = this.leftEdgeDay
        .plus({
          days: this.boxDayStartIdx + 6,
        })
        .setLocale(this.language).monthLong;
      if (firstDateMonth === secondDateMonth) {
        month = firstDateMonth;
        if (this.setMonths) {
          this.setMonths(month);
        }

        return month;
      } else {
        month = firstDateMonth + "/" + secondDateMonth;
        if (this.setMonths) {
          this.setMonths(month);
        }
        return month;
      }
    } else if (this.dateSpan === "month" && this.language) {
      const month = this.leftEdgeDay.setLocale(this.language).monthLong;
      if (this.setMonths) {
        this.setMonths(month);
      }
      return month
    }
    month = "";
    if (this.setMonths) {
      this.setMonths(month);
    }
    return month;
  }

  get boxDayStart() {
    return this.leftEdgeDay.plus({ days: this.boxDayStartIdx });
  }
  get boxDayEnd() {
    const daysToDisplay =
      Math.ceil((this.window.w - LEFT_W) / this.config.cell.w) +
      1 +
      this.config.preload * 2;
    const res = this.boxDayStart.plus({ days: daysToDisplay });
    return this.rightEdgeDay < res ? this.rightEdgeDay : res;
  }

  @Getter("contingent/isLoading") loading?: boolean;
  @Getter("contingent/contingentData") rows?: ContingentMonthView[];
  @Action("contingent/fetchHierarchy") fetchHierarchy?: (
    payload: ContingentDataRequest
  ) => Promise<any>;

  @Getter("contingent/currentDay") currentDay?: DateTime;
  @Action("contingent/goToDay") goToDay?: (payload: DateTime) => void;
  @Action("contingent/setMonths") setMonths?: (payload: string) => void;
  @Getter("globalFilters/filteredProperties") properties?: GlobalFilterProperty[];

  @Action("setCustomBreadcrumbs") setCustomBreadcrumbs?: (
    payload: string[] | undefined
  ) => Promise<void>;
  @Getter("customBreadcrumbs") customBreadcrumbs?: string[];

  @Watch("currentDay")
  currentDayChanged(fresh: DateTime) {
    if (
      this.initialNavTo ||
      fresh.toISODate() !== this.internalCurrentDay?.toISODate()
    ) {
      this.navTo(fresh);
    }
  }

  @Watch("dateSpan")
  dateSpanChanged() {
    if(this.currentDay) {
      this.navTo(this.currentDay);
    }
  }

  @Watch("propertyId")
  propertyIdChanged() {
    if(this.currentDay) {
      this.navTo(this.currentDay);
    }
  }

  @Watch("ratePlanId")
  ratePlanIdChanged() {
    this.calculateOffsets();
  }

  @Watch("showPrice")
  showPriceChanged() {
    this.calculateOffsets();
  }

  getFrom(day: DateTime) {
    let toReturn;
    switch (this.dateSpan) {
      case "week":
        toReturn = day.startOf("week");
        break;

      case "month":
        toReturn = day.startOf("month");
        break;

      case "year":
        toReturn = day.startOf("year");
        break;
    
      default:
        toReturn = day.startOf("month");
        break;
    }

    return toReturn
  }

  getTo(day: DateTime) {
    let toReturn;
    switch (this.dateSpan) {
      case "week":
        toReturn = day.endOf("week");
        break;

      case "month":
        toReturn = day.endOf("month");
        break;

      case "year":
        toReturn = day.endOf("year");
        break;
    
      default:
        toReturn = day.endOf("month");
        break;
    }

    return toReturn
  }

  sanitizeDate(val: DateTime) {
    return new Date(val.toISODate());
  }

  get propertyIdForHierarchy() {

    if(this.currentSettings?.selectedPropertyId == undefined || this.currentSettings?.selectedPropertyId == 0)
    {
      return this.properties?.find(x => x.status)?.propertyId ?? this.properties?.find(x => !x.status)?.propertyId ?? 0
    }
    else
    {
      //check if currently selected property is stuck in settings from before hiding it in filtered properties or refreshing filtered properties or whatever
      const property = this.properties?.find(x => x.propertyId == this.currentSettings?.selectedPropertyId);

      if(property == undefined || property == null)
      {
        if(this.properties && this.properties.length > 0)
        {
          this.currentSettings.selectedPropertyId = this.properties[0].propertyId;
          return this.properties[0].propertyId;
        }
      }
      else
      {
        return property.propertyId;
      }

      return 0;
    }
  }


  initialNavTo = true;
  internalCurrentDay?: DateTime;

  navTo(day: DateTime | string) {
    this.setBounds();
    this.initialNavTo = false;

    const _day = day instanceof DateTime ? day : DateTime.fromISO(day);
    this.internalCurrentDay = _day;

    const from = this.getFrom(_day);
    const to = this.getTo(_day);

    const params = {
      from: this.sanitizeDate(from),
      to: this.sanitizeDate(to),
      propertyId: this.propertyIdForHierarchy,
      cell: this.config.cell 
    } as ContingentDataRequest

    this.fetchHierarchy &&
      this.fetchHierarchy(params).then(() => {
        this.contingentData = this.rows ?? [];
        this.leftEdgeDay = from;
        this.rightEdgeDay = to;
        const propName = this.properties?.find((x) => x.propertyId == this.propertyIdForHierarchy)?.name ?? "";
        const breadcrumbs = [
          this.$t("Contingent").toString() + ": " + propName
        ];
        this.setCustomBreadcrumbs &&
          this.setCustomBreadcrumbs(breadcrumbs);

        this.window.l = this.config.preload * this.config.cell.w;
        this.$el.scrollTo({ left: this.window.l });

        this.calculateOffsets();
      });
  }

  mounted() {
    this.setBounds();

    const goTo = this.currentDay?.day === DateTime.local().day ? DateTime.local() : this.currentDay ?? DateTime.local();

    const from = this.getFrom(goTo);
    const to = this.getTo(goTo);

    this.leftEdgeDay = from;
    this.rightEdgeDay = to;
    this.$nextTick(() => this.$el.scrollTo({ left: this.window.l }));
    this.goToDay && this.goToDay(goTo);
  }

  toggleExpand(item: ContingentExpandableRow) {
    item.expanded = !item.expanded;
    this.calculateOffsets();
  }

  resized() {
    this.setBounds();
  }

  dragging = false;

  scrolled() {
    if (this.editor && this.editor.menu) {
      this.editor.close();
    }
  }

  setBounds() {
    this.window.w = this.$el.clientWidth;
    this.window.h = this.$el.clientHeight;

    let cellw = 48;
    switch (this.dateSpan) {
      case "week":
        cellw = Math.max(48, Math.floor((this.window.w - LEFT_W) / 7));
        break;
      default:
        cellw = Math.max(48, Math.floor((this.window.w - LEFT_W) / 31));
        break;
    }

    if (this.config.cell.w !== cellw) {
      this.$el.scrollLeft = Math.floor(
        (this.window.l / this.config.cell.w) * cellw
      );
      this.config.cell = { ...this.config.cell, w: cellw };
    }
  }

  getPriceEditorType() {
    return this.dateSpan === "week" ? "contingentPriceWeek" : "contingentPriceMonthYear";
  }

  openPriceEditor(
    rows: ContingentRowIdentifier[],
    event: any
  ) {
    if (!this.editor) return;
    this.editor.open(
      null,
      null,
      rows[0].ratePlan,
      null,
      this.getPriceEditorType(),
      this.orderedSelectDatePeriod[0],
      this.orderedSelectDatePeriod[1],
      { x: event.topLeft.x, y: event.topLeft.y + 48 },
      0,
      rows,
      false,
      false
    );
  }

  selectDatePeriod: DateTime[] = [];

  get orderedSelectDatePeriod() {
    return this.selectDatePeriod.sort(
      (a, b) => a.toMillis() - b.toMillis()
    );
  }

  // we reference the array of ratePlan rows to find which two are clicked
  // and every row in between is selected
  getSelectedRows() {
    const selectedRows: ContingentRowIdentifier[] = [];
    if(this.ratePlanTrack) {
      const indexes: number[] = [];
      this.ratePlanTrack.filter((x) => x.clicked).forEach((s) => {
        if(this.ratePlanTrack) {
          indexes.push(this.ratePlanTrack?.findIndex((x) => x == s));
        }
      });
      if(indexes.length == 1) {
        indexes.push(indexes[0]);
      }
      for (let index = indexes[0]; index <= indexes[1]; index++) {
        const element = this.ratePlanTrack[index];
        if(element.identifier) {
          selectedRows.push(element.identifier);
          element.clicked = false;
          element.firstClick = null;
        }
      }
    }
    return selectedRows;
  }

  checkIfIsSameCell() {
    return this.selectDatePeriod[0].toISODate() == this.selectDatePeriod[1].toISODate()
      && this.selection.row[0].month == this.selection.row[1].month
      && this.selection.row[0].unitType == this.selection.row[1].unitType;
  }

  selectFirstClick: any;

  select(event: any, ut: ContingentUnitTypeView, m: ContingentMonthView) {
    if(this.selectDatePeriod.length == 0) {
      this.selectFirstClick = event;
    }
    this.selectDatePeriod.push(event.day);
    const field = { month: this.dateSpan == "week" ? 0 : m.id, unitType: ut.id};
    this.selection.row.push(field);
    this.selection.selection = [event.day, event.day];
    if(this.selectDatePeriod.length == 2) {
      // two clicks on the same cell means we want to deselect that cell
      if(!this.checkIfIsSameCell()) {
        const selectedRows = this.getSelectedRows();
        const rows = [];
        for (let index = 0; index < selectedRows.length; index++) {
          const element = selectedRows[index];
          rows.push({ month: element.month, unitType: element.unitType });
        }
        this.selection = {
          row: rows,
          selection: [this.orderedSelectDatePeriod[0], this.orderedSelectDatePeriod[1]] 
        }
        this.openPriceEditor(
          selectedRows,
          this.selectFirstClick
        );
      }
      else {
        this.selection = { row: [], selection: null }
      }
      this.selectDatePeriod = [];
    }
  }

  single(event: any, ut: ContingentUnitTypeView, m: ContingentMonthView) {
    this.selectFirstClick = event;
    this.selectDatePeriod = [event.from, event.to];
    this.selection = {
      row: [{ month: this.dateSpan == "week" ? 0 : m.id, unitType: ut.id }],
      selection: [event.from, event.to] 
    }
    const selectedRow = this.getPriceRowIdentifier(m, ut);
    this.openPriceEditor(
      [selectedRow],
      this.selectFirstClick
    );
  }

  getRowName(r: ContingentMonthView) {
    if(this.language) {
      if(this.dateSpan == "week") {
        return this.leftEdgeDay.month == this.rightEdgeDay.month
          ? this.leftEdgeDay.setLocale(this.language).monthLong
          : this.leftEdgeDay.setLocale(this.language).monthLong + "/" + this.rightEdgeDay.setLocale(this.language).monthLong;
      }
      return DateTime.fromISO(r.representationDate).setLocale(this.language).monthLong;
    }
    return DateTime.fromISO(r.representationDate).monthLong;
  }

  getPriceRowIdentifier(r: ContingentMonthView, ut: ContingentUnitTypeView) {
    return {
      month: this.dateSpan == "week" ? 0 : r.id,
      unitType: ut.id,
      ratePlan: this.shownRatePlan(ut) ?? null
    } as ContingentRowIdentifier;
  }

  getRowStartDate(r: ContingentMonthView) {
    return DateTime.fromISO(r.unitTypes[0].daysData[0].date);
  }
  getRowEndDate(r: ContingentMonthView) {
    const dateEndIndex = r.unitTypes[0].daysData.length - 1;
    return DateTime.fromISO(r.unitTypes[0].daysData[dateEndIndex].date).endOf("day");
  }

  hasAvailability(ut: ContingentUnitTypeView, day: DateTime) {
    return this.occupied(ut, day) != 0 || this.available(ut, day) != 0;
  }
  hasAvailiabilityTotalRow(r: ContingentMonthView, day: DateTime) {
    return this.totalOccupied(r, day) != 0 || this.totalAvailable(r, day) != 0;
  }

  occupied(ut: ContingentUnitTypeView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    return ut.daysData.find(d => d.date.substring(8, 10) === ds)?.occupied;
  }
  totalOccupied(r: ContingentMonthView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    const totalOccupied = this.contingentData.find((x) => x.id == r.id)?.unitTypes
      .reduce(
        (occ, x) =>
        occ + (x.daysData.find((d) => d.date.substring(8, 10) == ds)?.occupied ?? 0),
        0
      ) ?? 0;
    return totalOccupied;
  }

  available(ut: ContingentUnitTypeView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    return ut.daysData.find(d => d.date.substring(8, 10) === ds)?.available;
  }
  totalAvailable(r: ContingentMonthView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    const totalAvailable = this.contingentData.find((x) => x.id == r.id)?.unitTypes
      .reduce(
        (av, x) =>
        av + (x.daysData.find((d) => d.date.substring(8, 10) == ds)?.available ?? 0),
        0
      ) ?? 0;
    return totalAvailable;
  }

  occupancyPercentage(ut: ContingentUnitTypeView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    return ut.daysData.find(d => d.date.substring(8, 10) === ds)?.occupancyPercentage;
  }
  averageOccupancyPercentage(r: ContingentMonthView, day: DateTime)
  {
    const occ = this.totalOccupied(r, day);
    const av = this.totalAvailable(r, day);
    const total = av + occ;
    return occ / total;
  }

  shownRatePlan(ut: ContingentUnitTypeView) {
    return ut.ratePlans.find((x) => x.id == this.currentSettings?.selectedRatePlanId);
  }

  price(rp: ContingentRatePlanView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);

    const item = rp.ratePlanData.find(d => d.date.substring(8, 10) === ds);

    const price = item?.initializedOnly ? undefined : item?.price;

    return price != undefined ? this.$n(price, { maximumFractionDigits: 0 }) : "";
  }

  priceDifference(rp: ContingentRatePlanView, day: DateTime)
  {
    const ds = day.toISODate().substring(8, 10);
    const item = rp.ratePlanData.find(d => d.date.substring(8, 10) === ds);

    return item?.initializedOnly ? undefined : item?.basePriceDifference;
  }

  demandChangingEvent(ut: ContingentUnitTypeView, day: DateTime) {
    const ds = day.toISODate().substring(8, 10);
    const demandData = ut.daysData.find(d => d.date.substring(8, 10) === ds)?.demandData;

    if(demandData){
      const highestDemand = Math.max(...demandData.map((d) => d.statusId));
      const statusName = this.demandEventStatuses?.find((s) => s.id == highestDemand)?.name ?? "";
      return "event-status-" + statusName.toLowerCase();
    }
    return '';
  }

  calculateOffsets() {
    let offset = 0;
    for (const r of this.contingentData) {
      r.height = r.h(
        this.config.cell,
        this.ratePlanId,
        this.currentSettings?.showPrice ?? false,
      );
      r.offset = offset;
      offset += r.height;
    }
  }
}
